import * as pc from 'playcanvas';
import { matCache } from '../utils/MaterialCache';
import { TEAM_COLORS } from '../shared';

/**
 * FlagEntity — a 3-D flag in the CTF arena.
 *
 * Visual: a thin vertical pole (cylinder) with a coloured rectangular cloth
 * (box) attached at the top.  When carried the cloth glows; when at base it
 * pulses gently via a tiny emissive.
 */
export class FlagEntity {
  readonly team: 'red' | 'blue';
  readonly entity: pc.Entity;

  private clothMat: pc.StandardMaterial;
  private baseGlowMat: pc.StandardMaterial;
  private baseRing: pc.Entity;

  constructor(app: pc.Application, team: 'red' | 'blue', x: number, z: number) {
    this.team = team;

    const col = TEAM_COLORS[team];
    const [er, eg, eb] = col.emissive;

    this.entity = new pc.Entity(`flag_${team}`);
    app.root.addChild(this.entity);
    this.entity.setPosition(x, 0, z);

    // ── Pole ──────────────────────────────────────────────────────────────
    const pole = new pc.Entity('pole');
    pole.addComponent('render', { type: 'cylinder' });
    pole.render!.castShadows = false;
    pole.render!.receiveShadows = false;
    pole.render!.meshInstances[0].material = matCache.get({
      diffuse: new pc.Color(0.7, 0.7, 0.7),
    });
    pole.setLocalScale(0.12, 3.0, 0.12);
    pole.setLocalPosition(0, 1.5, 0);
    this.entity.addChild(pole);

    // ── Cloth ─────────────────────────────────────────────────────────────
    const cloth = new pc.Entity('cloth');
    cloth.addComponent('render', { type: 'box' });
    cloth.render!.castShadows = false;
    cloth.render!.receiveShadows = false;
    // createUnique: emissive is mutated in setCarried / setAtBase
    this.clothMat = matCache.createUnique({
      diffuse: new pc.Color(er, eg, eb),
      emissive: new pc.Color(er * 0.3, eg * 0.3, eb * 0.3),
      emissiveIntensity: 1.0,
    });
    cloth.render!.meshInstances[0].material = this.clothMat;
    cloth.setLocalScale(0.8, 0.55, 0.08);
    cloth.setLocalPosition(0.46, 2.88, 0);   // hangs off the top of the pole
    this.entity.addChild(cloth);

    // ── Base glow ring (flat disc on ground) ──────────────────────────────
    this.baseRing = new pc.Entity('baseRing');
    this.baseRing.addComponent('render', { type: 'cylinder' });
    this.baseRing.render!.castShadows = false;
    this.baseRing.render!.receiveShadows = false;
    this.baseGlowMat = matCache.createUnique({
      diffuse:    new pc.Color(er * 0.4, eg * 0.4, eb * 0.4),
      emissive:   new pc.Color(er, eg, eb),
      emissiveIntensity: 1.2,
      blendType:  pc.BLEND_NORMAL,
      depthWrite: false,
    });
    this.baseGlowMat.opacity = 0.55;
    this.baseGlowMat.update();
    this.baseRing.render!.meshInstances[0].material = this.baseGlowMat;
    this.baseRing.setLocalScale(3.0, 0.04, 3.0);
    this.baseRing.setLocalPosition(0, 0.03, 0);
    this.entity.addChild(this.baseRing);
  }

  setPosition(x: number, z: number) {
    this.entity.setPosition(x, 0, z);
  }

  /** Flag is sitting at home base. */
  setAtBase() {
    this.entity.enabled = true;
    this.baseRing.enabled = true;
    this.clothMat.emissiveIntensity = 1.0;
    this.clothMat.update();
    this.baseGlowMat.opacity = 0.55;
    this.baseGlowMat.update();
  }

  /** Flag has been picked up and is being carried. */
  setCarried() {
    // Hide the world entity — the carrier's tank visually represents the flag
    // via a glow effect handled in app.ts.  We just disable here.
    this.entity.enabled = false;
  }

  /** Flag has been dropped at a world position (not home base). */
  setDropped(x: number, z: number) {
    this.entity.enabled = true;
    this.baseRing.enabled = false;
    this.entity.setPosition(x, 0, z);
    this.clothMat.emissiveIntensity = 0.4;
    this.clothMat.update();
  }

  /** Flag has been returned to its home base. */
  setReturned(homeX: number, homeZ: number) {
    this.entity.setPosition(homeX, 0, homeZ);
    this.setAtBase();
  }

  destroy() {
    this.entity.destroy();
  }
}
